import React from 'react';
import { Box, CircularProgress, Chip } from '@mui/material';
import { Check, Error, FiberManualRecord } from '@mui/icons-material';

/**
 * Save Status Indicator Component
 *
 * Displays the current save status with appropriate icon and color:
 * - idle: Gray dot with "Saved"
 * - dirty: Yellow dot with "Unsaved"
 * - saving: Spinner with "Saving..." text
 * - saved: Green checkmark with relative timestamp
 * - error: Red error icon with error message
 *
 * @param {Object} props
 * @param {'idle'|'dirty'|'saving'|'saved'|'error'} props.status - Current save status
 * @param {string|null} props.error - Error message (if status is 'error')
 * @param {string|null} props.lastSavedAt - ISO timestamp of last save
 * @returns {JSX.Element}
 */
export function SaveStatusIndicator({ status, error, lastSavedAt }) {
  const getStatusConfig = () => {
    switch (status) {
      case 'dirty':
        return {
          icon: <FiberManualRecord sx={{ fontSize: 12, color: 'warning.main' }} />,
          label: 'Unsaved',
          color: 'warning'
        };
      case 'saving':
        return {
          icon: <CircularProgress size={16} />,
          label: 'Saving...',
          color: 'default'
        };
      case 'saved':
        return {
          icon: <Check />,
          label: lastSavedAt
            ? `Saved ${formatRelativeTime(lastSavedAt)}`
            : 'Saved',
          color: 'success'
        };
      case 'error':
        return {
          icon: <Error />,
          label: error || 'Save failed',
          color: 'error'
        };
      default: // idle
        return {
          icon: <FiberManualRecord sx={{ fontSize: 12, color: 'text.disabled' }} />,
          label: 'Saved',
          color: 'default'
        };
    }
  };

  const config = getStatusConfig();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    </Box>
  );
}

/**
 * Format ISO timestamp to relative time string
 * Examples: "just now", "2m ago", "3h ago", "2d ago"
 *
 * @param {string} isoTimestamp - ISO 8601 timestamp
 * @returns {string} Relative time string
 */
function formatRelativeTime(isoTimestamp) {
  const now = new Date();
  const then = new Date(isoTimestamp);
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}

export default SaveStatusIndicator;
